import { Link } from "react-router-dom";
import { Phone, Mail, MapPin, Clock, Siren } from "lucide-react";
import { SITE, SERVICES } from "../lib/site";
import { CITY_CONTENT, CITY_SLUGS } from "../lib/cityContent";

export default function Footer() {
  return (
    <footer data-testid="site-footer" className="bg-black border-t border-white/10 mt-20">
      <div className="max-w-[1400px] mx-auto px-5 md:px-8 py-16 grid grid-cols-1 md:grid-cols-12 gap-10">
        <div className="md:col-span-4">
          <Link to="/" className="flex items-center gap-3">
            <img src={SITE.logo} alt="Alliance Crane Service" className="h-14 w-14 object-contain" />
            <div className="leading-tight">
              <div className="font-display text-xl">ALLIANCE</div>
              <div className="font-display text-[11px] text-[var(--ac-red)] tracking-[0.22em]">CRANE SERVICE LLC</div>
            </div>
          </Link>
          <p className="mt-5 text-sm text-zinc-400 leading-relaxed max-w-sm">
            Licensed, insured mobile crane services based in DeLand, FL. Serving Central Florida with safe, on-schedule
            lifts for HVAC, steel, machinery, trusses and emergency response.
          </p>
        </div>

        <div className="md:col-span-3">
          <div className="overline mb-4">Services</div>
          <ul className="space-y-2">
            {SERVICES.map((s) => (
              <li key={s.slug}>
                <Link
                  to={`/services/${s.slug}`}
                  className="text-sm text-zinc-300 hover:text-[var(--ac-red)] transition-colors"
                >
                  {s.title}
                </Link>
              </li>
            ))}
            <li className="pt-2">
              <Link
                to="/resources"
                data-testid="footer-resources"
                className="text-sm font-semibold text-[var(--ac-red)] hover:text-white transition-colors inline-flex items-center gap-1"
              >
                Resources &amp; Guides →
              </Link>
            </li>
          </ul>
        </div>

        <div className="md:col-span-3">
          <div className="overline mb-4">Service Area</div>
          <ul className="grid grid-cols-2 gap-y-2 gap-x-3">
            {CITY_SLUGS.slice(0, 10).map((slug) => (
              <li key={slug} className="text-sm">
                <Link
                  to={`/service-area/${slug}`}
                  className="text-zinc-300 hover:text-[var(--ac-red)] transition-colors"
                >
                  {CITY_CONTENT[slug].name}, FL
                </Link>
              </li>
            ))}
          </ul>
        </div>

        <div className="md:col-span-2">
          <div className="overline mb-4">Contact</div>
          <ul className="space-y-3 text-sm text-zinc-300">
            <li className="flex items-start gap-2">
              <Phone className="w-4 h-4 mt-0.5 text-[var(--ac-red)]" />
              <a href={`tel:${SITE.phoneE164}`} className="font-mono-data" data-testid="footer-phone">
                {SITE.phone}
              </a>
            </li>
            <li className="flex items-start gap-2">
              <Mail className="w-4 h-4 mt-0.5 text-[var(--ac-red)]" />
              <a href={`mailto:${SITE.email}`} className="break-all" data-testid="footer-email">
                {SITE.email}
              </a>
            </li>
            <li className="flex items-start gap-2">
              <MapPin className="w-4 h-4 mt-0.5 text-[var(--ac-red)]" />
              <span>{SITE.address}</span>
            </li>
            <li className="flex items-start gap-2">
              <Clock className="w-4 h-4 mt-0.5 text-[var(--ac-red)]" />
              <span>{SITE.hours}</span>
            </li>
            <li className="flex items-start gap-2">
              <Siren className="w-4 h-4 mt-0.5 text-[var(--ac-red)]" />
              <span className="font-semibold">{SITE.emergencyHours}</span>
            </li>
            <li className="pt-2">
              <a
                href={SITE.googleReviewUrl}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="footer-google-review"
                className="inline-flex items-center gap-2 font-mono-data uppercase text-[10px] tracking-[0.18em] text-[var(--ac-yellow)] hover:text-white transition-colors"
              >
                <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                  <path d="M12 2l2.9 6.9 7.1.6-5.4 4.7 1.6 7-6.2-3.8-6.2 3.8 1.6-7L2 9.5l7.1-.6L12 2z"/>
                </svg>
                Leave a Google Review
              </a>
            </li>
          </ul>
        </div>
      </div>

      <div className="border-t border-white/10">
        <div className="max-w-[1400px] mx-auto px-5 md:px-8 py-6 flex flex-col md:flex-row items-center justify-between gap-3 text-xs text-zinc-500">
          <div>© {new Date().getFullYear()} {SITE.name}. All rights reserved.</div>
          <div className="flex flex-wrap items-center justify-center md:justify-end gap-x-4 gap-y-1 font-mono-data tracking-wider">
            <a
              href="https://www.claimbeast.com"
              target="_blank"
              rel="noopener"
              data-testid="footer-partner-claimbeast"
              className="hover:text-white transition-colors"
            >
              Partner: Claim Beast
            </a>
            <a
              href="https://www.elementroofingsolutions.com"
              target="_blank"
              rel="noopener"
              data-testid="footer-partner-element-roofing"
              className="hover:text-white transition-colors"
            >
              Partner: Element Roofing Solutions
            </a>
            <span>DELAND · FL · USA</span>
          </div>
        </div>
      </div>
    </footer>
  );
}
